import { Alert, Badge, Button, Card, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Mail, Plus, ShieldCheck, ShieldOff, UserCog, UserMinus, UserX } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { ErreurChargement, InfoList, Section } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { Drawer } from '../../components/Drawer';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { TabPanel, Tabs } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../lib/format';
import type { StatutCompte } from '../../lib/labels';
import { PageHeader } from '../../pages/PageHeader';
import { AuditTimeline } from '../audit/AuditTimeline';

import { ChoixRole, eleveDeErreur, PromotionDepuisInvitation, type EleveExistant } from './PromotionEleve';

type Compte = components['schemas']['AdminAccount'];

export function statutAdmin(a: Pick<Compte, 'isActive' | 'invitationPending'>): StatutCompte {
  if (!a.isActive) return { libelle: 'Désactivé', ton: 'neutral' };
  if (a.invitationPending) return { libelle: 'Invitation en attente', ton: 'gold' };
  return { libelle: 'Actif', ton: 'success' };
}

export function nomAdmin(a: Pick<Compte, 'firstName' | 'lastName' | 'username' | 'email'>): string {
  return [a.firstName, a.lastName].filter(Boolean).join(' ') || a.username || a.email;
}

function useAdmins() {
  return useQuery({ queryKey: ['admins'], queryFn: async () => (await lire(api.GET('/api/admin/admins'))).admins });
}

/**
 * Administrateurs (cahier des charges §7.11), réservé au super admin :
 * invitations, rôle, désactivation, réinitialisations, et le réglage de la
 * double authentification obligatoire (§9). Fiche en panneau (/administrateurs/:id).
 */
export function AdminsPage() {
  const navigate = useNavigate();
  const { id: ouvert } = useParams();
  const admins = useAdmins();
  const [inviter, setInviter] = useState(false);

  const colonnes: Colonne<Compte>[] = [
    {
      id: 'nom',
      header: 'Administrateur',
      cell: (a) => (
        <div>
          <div className="cell-main">{nomAdmin(a)}</div>
          <div className="cell-sub">{a.email}</div>
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Rôle',
      cell: (a) => <Badge tone={a.isSuperAdmin ? 'terracotta' : 'gold'}>{a.isSuperAdmin ? 'Super admin' : 'Admin'}</Badge>,
    },
    {
      id: 'statut',
      header: 'Statut',
      cell: (a) => {
        const s = statutAdmin(a);
        return <Badge tone={s.ton}>{s.libelle}</Badge>;
      },
    },
    {
      id: '2fa',
      header: 'Double auth.',
      cell: (a) =>
        a.twoFactorEnabled ? (
          <span className="avec-icone">
            <ShieldCheck size={14} aria-hidden="true" /> Activée
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    { id: 'connexion', header: 'Dernière connexion', cell: (a) => (a.lastLoginAt ? formatDateTime(a.lastLoginAt) : <span className="muted">Jamais</span>) },
    { id: 'invite', header: 'Ajouté par', secondaire: true, cell: (a) => a.createdBy?.email ?? <span className="muted">—</span> },
  ];

  return (
    <>
      <PageHeader
        title="Administrateurs"
        lead="Les comptes de l’équipe. Un admin gère les élèves, les crédits et les clés ; un super admin gère aussi les ventes, les contenus IA, les événements et les administrateurs."
        actions={
          <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => setInviter(true)}>
            Inviter un administrateur
          </Button>
        }
      />
      <ReglageDoubleAuth />
      <Section title="Comptes">
        {admins.isError ? (
          <ErreurChargement erreur={admins.error} onReessayer={() => void admins.refetch()} />
        ) : (
          <DataTable
            libelle="Administrateurs"
            colonnes={colonnes}
            lignes={admins.data}
            cleLigne={(a) => a.id}
            chargement={admins.isLoading}
            onOuvrir={(a) => navigate(`/administrateurs/${a.id}`)}
            estActive={(a) => a.id === ouvert}
            vide={<EmptyState icon={<UserCog size={26} aria-hidden="true" />} title="Aucun administrateur" />}
          />
        )}
      </Section>
      <InvitationForm open={inviter} onOpenChange={setInviter} />
      <Outlet />
    </>
  );
}

/** Double authentification obligatoire pour les super admins : activée à la bascule. */
function ReglageDoubleAuth() {
  const toast = useToast();
  const client = useQueryClient();
  const { user } = useSession();
  const [confirmer, setConfirmer] = useState(false);
  const reglage = useQuery({ queryKey: ['securite'], queryFn: () => lire(api.GET('/api/admin/settings/security')) });
  const r = reglage.data;

  if (reglage.isError) return <ErreurChargement erreur={reglage.error} onReessayer={() => void reglage.refetch()} />;
  if (!r) return <Skeleton height={96} />;

  const exiger = !r.twoFactorRequiredForSuperAdmins;
  const sansDoubleAuth = r.superAdminsWithoutTwoFactor.filter((e) => e !== user?.email);

  return (
    <Card className="bandeau-securite">
      <div className="bandeau-securite__texte">
        <div className="bandeau-securite__titre">
          {r.twoFactorRequiredForSuperAdmins ? <ShieldCheck size={16} aria-hidden="true" /> : <ShieldOff size={16} aria-hidden="true" />}
          Double authentification des super admins : {r.twoFactorRequiredForSuperAdmins ? 'obligatoire' : 'facultative'}
        </div>
        <p className="texte-aide">
          {!r.twoFactorAvailable
            ? 'Pas encore configurée sur le serveur : l’équipe technique doit ajouter la clé de chiffrement.'
            : r.twoFactorRequiredForSuperAdmins
              ? 'Chaque super admin saisit un code du téléphone à la connexion. Les admins peuvent l’activer depuis « Mon compte ».'
              : 'Proposée à tous depuis « Mon compte ». Rendez-la obligatoire pour les super admins quand l’équipe est prête.'}
          {r.superAdminsWithoutTwoFactor.length > 0 && (
            <> Sans double authentification : {r.superAdminsWithoutTwoFactor.join(', ')}.</>
          )}
        </p>
      </div>
      <Button size="sm" variant={exiger ? 'primary' : 'secondary'} disabled={!r.twoFactorAvailable} onClick={() => setConfirmer(true)}>
        {exiger ? 'Rendre obligatoire' : 'Rendre facultative'}
      </Button>
      <ConfirmDialog
        open={confirmer}
        onOpenChange={setConfirmer}
        title={exiger ? 'Rendre la double authentification obligatoire' : 'Rendre la double authentification facultative'}
        confirmLabel={exiger ? 'Rendre obligatoire' : 'Rendre facultative'}
        tone={exiger ? 'primary' : 'danger'}
        onConfirm={async () => {
          await lire(api.PUT('/api/admin/settings/security', { body: { twoFactorRequiredForSuperAdmins: exiger } }));
          toast.succes(exiger ? 'Double authentification obligatoire pour les super admins.' : 'Double authentification facultative.');
          void client.invalidateQueries({ queryKey: ['securite'] });
          void client.invalidateQueries({ queryKey: ['2fa'] });
        }}
      >
        {exiger ? (
          <>
            <p>
              Chaque super admin devra saisir un code du téléphone à la connexion. Il faut l’avoir activée sur votre propre compte (« Mon
              compte ») avant.
            </p>
            <p>L’ancien back-office ne sait pas demander ce code : les super admins ne pourront plus s’en servir. À faire à la bascule.</p>
            {sansDoubleAuth.length > 0 && (
              <p>
                <strong>{sansDoubleAuth.join(', ')}</strong> devr{sansDoubleAuth.length > 1 ? 'ont' : 'a'} l’activer à sa prochaine action dans le
                back-office.
              </p>
            )}
          </>
        ) : (
          <p>Les super admins pourront se connecter avec le mot de passe seul. Ceux qui l’ont activée la gardent.</p>
        )}
      </ConfirmDialog>
    </Card>
  );
}

/** Invitation : le compte est créé sans mot de passe, l'e-mail contient le lien pour le choisir (valable 7 jours). */
function InvitationForm({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const id = useId();
  const toast = useToast();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [superAdmin, setSuperAdmin] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [eleve, setEleve] = useState<EleveExistant | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setPrenom('');
      setNom('');
      setSuperAdmin(false);
      setErreur(null);
      setEleve(null);
    }
  }, [open]);

  const valide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await lire(
        api.POST('/api/admin/admins/invite', {
          body: { email: email.trim(), firstName: prenom.trim() || undefined, lastName: nom.trim() || undefined, superAdmin },
        })
      );
      if (r.emailSent) toast.succes(`Invitation envoyée à ${r.admin.email}.`);
      else toast.erreur(r.message);
      void client.invalidateQueries({ queryKey: ['admins'] });
      void client.invalidateQueries({ queryKey: ['securite'] });
      onOpenChange(false);
      navigate(`/administrateurs/${r.admin.id}`);
    } catch (err) {
      // Adresse d'une élève : on propose de transformer son compte (une adresse ne porte qu'un compte).
      const existant = eleveDeErreur(err);
      if (existant) setEleve(existant);
      else setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  if (eleve) {
    return (
      <Modal open={open} onOpenChange={onOpenChange} title="Transformer un compte élève">
        <PromotionDepuisInvitation
          email={email.trim().toLowerCase()}
          eleve={eleve}
          superAdmin={superAdmin}
          onRetour={() => setEleve(null)}
          onFait={() => onOpenChange(false)}
        />
      </Modal>
    );
  }

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title="Inviter un administrateur">
      <form className="stack" onSubmit={soumettre} noValidate>
        <p className="texte-aide">
          Un e-mail part avec un lien pour choisir le mot de passe, valable 7 jours. Si l’adresse est celle d’un compte élève, vous pourrez
          transformer ce compte en compte administrateur.
        </p>
        {erreur && <Alert>{erreur}</Alert>}
        <Field id={`${id}-email`} label="E-mail">
          <Input id={`${id}-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus autoComplete="off" />
        </Field>
        <div className="grille-2">
          <Field id={`${id}-prenom`} label="Prénom (facultatif)">
            <Input id={`${id}-prenom`} value={prenom} onChange={(e) => setPrenom(e.target.value)} maxLength={80} />
          </Field>
          <Field id={`${id}-nom`} label="Nom (facultatif)">
            <Input id={`${id}-nom`} value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} />
          </Field>
        </div>
        <ChoixRole superAdmin={superAdmin} onChange={setSuperAdmin} />
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={!valide}>
            Envoyer l’invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type Action = 'role' | 'statut' | 'mdp' | 'invitation' | 'doubleAuth' | 'retrait';

/** Fiche d'un administrateur : informations, actions et journal. */
export function AdminDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const client = useQueryClient();
  const { user } = useSession();
  const admins = useAdmins();
  const [onglet, setOnglet] = useState<'fiche' | 'journal'>('fiche');
  const [action, setAction] = useState<Action | null>(null);

  const a = admins.data?.find((c) => c.id === id);
  const moiMeme = a?.id === user?.id;
  const fermer = () => navigate(`/administrateurs${location.search}`);

  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: ['admins'] });
    void client.invalidateQueries({ queryKey: ['securite'] });
    void client.invalidateQueries({ queryKey: ['audit', id] });
  };

  const modifier = async (body: { isSuperAdmin?: boolean; isActive?: boolean; resetTwoFactor?: true }, message: string) => {
    await lire(api.PATCH('/api/admin/admins/{id}', { params: { path: { id } }, body }));
    toast.succes(message);
    rafraichir();
  };

  if (admins.isError) {
    return (
      <Drawer title="Administrateur" onClose={fermer}>
        <ErreurChargement erreur={admins.error} onReessayer={() => void admins.refetch()} />
      </Drawer>
    );
  }
  if (admins.data && !a) {
    return (
      <Drawer title="Administrateur introuvable" onClose={fermer}>
        <p className="texte-aide">Ce compte n’est plus administrateur ou a été supprimé.</p>
      </Drawer>
    );
  }

  const s = a ? statutAdmin(a) : null;
  const actions: Array<{ id: Action; libelle: string; icone: ReactNode; visible: boolean; danger?: boolean }> = a
    ? [
        {
          id: 'role',
          libelle: a.isSuperAdmin ? 'Passer en admin' : 'Passer en super admin',
          icone: <UserCog size={14} aria-hidden="true" />,
          visible: a.isActive,
        },
        { id: 'invitation', libelle: 'Renvoyer l’invitation', icone: <Mail size={14} aria-hidden="true" />, visible: a.isActive && a.invitationPending },
        {
          id: 'mdp',
          libelle: 'Envoyer un lien de réinitialisation',
          icone: <KeyRound size={14} aria-hidden="true" />,
          visible: a.isActive && !a.invitationPending,
        },
        {
          id: 'doubleAuth',
          libelle: 'Réinitialiser la double authentification',
          icone: <ShieldOff size={14} aria-hidden="true" />,
          visible: a.twoFactorEnabled,
        },
        {
          id: 'retrait',
          libelle: 'Retirer les droits d’administration',
          icone: <UserMinus size={14} aria-hidden="true" />,
          visible: true,
          danger: true,
        },
        {
          id: 'statut',
          libelle: a.isActive ? 'Désactiver le compte' : 'Réactiver le compte',
          icone: <UserX size={14} aria-hidden="true" />,
          visible: true,
          danger: a.isActive,
        },
      ]
    : [];

  return (
    <Drawer
      onClose={fermer}
      label={a ? `Fiche de ${a.email}` : 'Fiche administrateur'}
      title={a ? nomAdmin(a) : <Skeleton width={200} height={22} />}
      subtitle={a?.email}
      badges={
        a && s ? (
          <>
            <Badge tone={a.isSuperAdmin ? 'terracotta' : 'gold'}>{a.isSuperAdmin ? 'Super admin' : 'Admin'}</Badge>
            <Badge tone={s.ton}>{s.libelle}</Badge>
            {a.twoFactorEnabled && <Badge tone="success">Double auth.</Badge>}
          </>
        ) : undefined
      }
    >
      {!a ? (
        <div className="stack" style={{ paddingTop: 16 }}>
          <Skeleton height={140} />
        </div>
      ) : (
        <>
          <Tabs
            label="Sections de la fiche"
            onglets={[
              { id: 'fiche', label: 'Compte' },
              { id: 'journal', label: 'Journal' },
            ]}
            actif={onglet}
            onChange={setOnglet}
          />
          <TabPanel label={onglet === 'fiche' ? 'Compte' : 'Journal'}>
            {onglet === 'journal' ? (
              <AuditTimeline targetId={a.id} />
            ) : (
              <>
                <InfoList
                  items={[
                    { label: 'E-mail', value: a.email },
                    { label: 'Ajouté le', value: formatDateTime(a.createdAt) },
                    { label: 'Ajouté par', value: a.createdBy?.email ?? '—' },
                    { label: 'Dernière connexion', value: a.lastLoginAt ? formatDateTime(a.lastLoginAt) : 'Jamais' },
                    { label: 'Double authentification', value: a.twoFactorEnabled ? 'Activée' : 'Non activée' },
                  ]}
                />
                <Section title="Actions">
                  {moiMeme ? (
                    <p className="texte-aide">
                      C’est votre compte : un autre super admin gère votre rôle. Votre double authentification se règle dans « Mon compte ».
                    </p>
                  ) : (
                    <div className="actions-fiche">
                      {actions
                        .filter((x) => x.visible)
                        .map((x) => (
                          <Button key={x.id} size="sm" variant={x.danger ? 'danger' : 'secondary'} icon={x.icone} onClick={() => setAction(x.id)}>
                            {x.libelle}
                          </Button>
                        ))}
                    </div>
                  )}
                </Section>
              </>
            )}
          </TabPanel>

          <ConfirmDialog
            open={action === 'role'}
            onOpenChange={(o) => !o && setAction(null)}
            title={a.isSuperAdmin ? 'Passer en admin' : 'Passer en super admin'}
            confirmLabel="Changer le rôle"
            onConfirm={() => modifier({ isSuperAdmin: !a.isSuperAdmin }, 'Rôle modifié.')}
          >
            {a.isSuperAdmin ? (
              <p>{a.email} ne pourra plus gérer les ventes, les contenus IA, les événements ni les administrateurs.</p>
            ) : (
              <p>{a.email} pourra tout gérer, y compris les administrateurs, les ventes et les contenus IA.</p>
            )}
          </ConfirmDialog>
          <ConfirmDialog
            open={action === 'statut'}
            onOpenChange={(o) => !o && setAction(null)}
            title={a.isActive ? 'Désactiver le compte' : 'Réactiver le compte'}
            confirmLabel={a.isActive ? 'Désactiver' : 'Réactiver'}
            tone={a.isActive ? 'danger' : 'primary'}
            retaper={a.isActive ? a.email : undefined}
            onConfirm={() => modifier({ isActive: !a.isActive }, a.isActive ? 'Compte désactivé.' : 'Compte réactivé.')}
          >
            {a.isActive ? (
              <p>{a.email} ne pourra plus se connecter au back-office. Son historique reste au journal ; le compte peut être réactivé.</p>
            ) : (
              <p>{a.email} pourra de nouveau se connecter avec son mot de passe.</p>
            )}
          </ConfirmDialog>
          <ConfirmDialog
            open={action === 'mdp'}
            onOpenChange={(o) => !o && setAction(null)}
            title="Envoyer un lien de réinitialisation"
            confirmLabel="Envoyer"
            onConfirm={async () => {
              await lire(api.PUT('/api/admin/users/{id}/password', { params: { path: { id } } }));
              toast.succes(`Lien envoyé à ${a.email}.`);
              rafraichir();
            }}
          >
            <p>Un e-mail part vers {a.email} avec un lien pour choisir un nouveau mot de passe (valable 1 heure). L’actuel reste valable d’ici là.</p>
          </ConfirmDialog>
          <ConfirmDialog
            open={action === 'retrait'}
            onOpenChange={(o) => !o && setAction(null)}
            title="Retirer les droits d’administration"
            confirmLabel="Retirer les droits"
            tone="danger"
            retaper={a.email}
            onConfirm={async () => {
              await lire(api.PATCH('/api/admin/admins/{id}', { params: { path: { id } }, body: { isAdmin: false } }));
              toast.succes(`${a.email} n’est plus administrateur.`);
              await Promise.all([['admins'], ['securite'], ['eleves'], ['eleve']].map((queryKey) => client.invalidateQueries({ queryKey })));
              navigate(`/eleves/${id}`);
            }}
          >
            <ul className="liste-aide note">
              <li>{a.email} ne peut plus ouvrir le back-office : ses sessions en cours se ferment tout de suite.</li>
              <li>
                Le compte redevient un compte élève, visible dans « Élèves », avec ses conversations, coachs et crédits. Ses crédits ne sont
                plus illimités et sa double authentification est retirée.
              </li>
              <li>
                {a.invitationPending
                  ? 'L’invitation n’a jamais été acceptée : le compte n’a pas de mot de passe. Pour l’utiliser comme élève, il faudra passer par « Mot de passe oublié ».'
                  : 'Son mot de passe reste valable pour l’application élève.'}
              </li>
              <li>Pour supprimer le compte, ouvrez ensuite sa fiche élève. L’opération est inscrite au journal d’audit.</li>
            </ul>
          </ConfirmDialog>
          <ConfirmDialog
            open={action === 'invitation'}
            onOpenChange={(o) => !o && setAction(null)}
            title="Renvoyer l’invitation"
            confirmLabel="Renvoyer"
            onConfirm={async () => {
              await lire(api.POST('/api/admin/admins/{id}/invitation', { params: { path: { id } } }));
              toast.succes(`Invitation renvoyée à ${a.email}.`);
              rafraichir();
            }}
          >
            <p>Un nouveau lien, valable 7 jours, part vers {a.email}.</p>
          </ConfirmDialog>
          <ConfirmDialog
            open={action === 'doubleAuth'}
            onOpenChange={(o) => !o && setAction(null)}
            title="Réinitialiser la double authentification"
            confirmLabel="Réinitialiser"
            tone="danger"
            retaper={a.email}
            onConfirm={() => modifier({ resetTwoFactor: true }, 'Double authentification réinitialisée.')}
          >
            <p>
              À utiliser quand {a.email} a perdu son téléphone et ses codes de secours, après avoir vérifié son identité autrement (appel,
              visio). Le téléphone actuel et les codes de secours ne vaudront plus rien.
            </p>
            <p>
              {a.isSuperAdmin
                ? 'Si la double authentification est obligatoire, ce compte devra la réactiver à sa prochaine connexion.'
                : 'Ce compte pourra la réactiver depuis « Mon compte ».'}
            </p>
          </ConfirmDialog>
        </>
      )}
    </Drawer>
  );
}
